import ArrowBack from '@mui/icons-material/ArrowBack';
import HourglassEmptyOutlined from '@mui/icons-material/HourglassEmptyOutlined';
import { Link } from 'react-router';

import Avatar from '@/auth/components/Avatar';
import Meter from '@/componentsv2/Meter';

import type { ViewerRole } from '../components/EngagementHub/roles';
import { viewerSeesExpertRate } from '../components/EngagementHub/roles';
import StatusBadge from './StatusBadge';
import { formatEngagementMoney, formatHours } from './money';
import { counterpartyHeading } from './parties';
import type { EngagementView, ProjectView } from './viewModel';

interface HubHeaderProps {
  view: EngagementView;
  viewerRole: ViewerRole;
}

interface MetricProps {
  label: string;
  children: React.ReactNode;
}

function Metric({ label, children }: MetricProps) {
  return (
    <div>
      <div className="text-on-surface-muted subtitle-2">{label}</div>
      <div className="mt-4 text-on-surface hd-5">{children}</div>
    </div>
  );
}

/**
 * The counterparty's face, matching the prototype's `IdentityTile`
 * (`components/EngagementHub/Header.tsx:72`) at the same 56px.
 *
 * The expert's arm is the customer organization's initials on a rounded
 * square, as the prototype draws it: the 8px corner (`rounded-md`, which beats
 * the Avatar's circle because MUI styles are injected first) is what tells an
 * organization from a person's circle. `customerOrganizationName` (ENG-5749)
 * is its source. With no name (unresolvable, or withheld by the customer's
 * disclosure) there is nothing to draw, so the tile goes and the H1 keeps its
 * neutral "Engagement". It never falls back to the expert's own face, which
 * would name the wrong party.
 *
 * Both faces are `aria-hidden`: the H1 beside them already names the
 * counterparty, so an exposed tile makes a screen reader announce the initials
 * first ("NA, Northwind Advisory"), as the message avatars would.
 */
function IdentityTile({ view, viewerRole }: HubHeaderProps) {
  if (viewerRole === 'expert') {
    if (!view.customerOrganizationName) return null;
    return (
      <Avatar
        fullName={view.customerOrganizationName}
        className="shrink-0 rounded-md"
        sx={{ width: 56, height: 56 }}
        aria-hidden
      />
    );
  }

  if (!view.expertIdentity) {
    // `customer_introduced` engagements exist before their expert does, and the
    // placeholder says which of the two nulls this is: awaiting, not missing.
    return (
      <div
        className="flex h-56 w-56 shrink-0 items-center justify-center rounded-full border border-dashed border-surface-border bg-surface-elevated text-on-surface-muted"
        aria-label="Awaiting expert"
      >
        <HourglassEmptyOutlined sx={{ fontSize: 24 }} />
      </div>
    );
  }

  return (
    // No `profileId`: it hangs an onClick off the MUI Avatar with no tabIndex or
    // key handler, which is a click target a keyboard user cannot reach. The
    // heading beside it is a real link to the same profile, and the prototype's
    // own tile is decorative for the same reason.
    <Avatar
      fullName={view.expertIdentity.name ?? 'Expert'}
      src={view.expertIdentity.pictureUrl ?? undefined}
      className="shrink-0"
      sx={{ width: 56, height: 56 }}
      aria-hidden
    />
  );
}

/**
 * `(<group name>: CODE)`, mirroring `ProjectBar`'s `titleSufix` on the
 * consultation page — where the same slot renders e.g. `(OnFrontiers Prod Test
 * Team: 0311)`, the group's own name rather than the literal word "Team". The
 * tracking code goes to everyone who can see the project.
 *
 * The group name is narrower than "OnFrontiers staff": it keys on the resolved
 * `of_fulfillment` role, which `deriveViewerRoleFromSubject` ranks below every
 * engagement role. Staff who are also the work approver resolve as
 * `work_approver` and get the customer's suffix. That is the safe direction —
 * staff see less, never more — and it is pinned by a test.
 */
function projectSuffix(project: ProjectView, viewerRole: ViewerRole): string | null {
  const org = viewerRole === 'of_fulfillment' ? project.organizationName : null;
  const code = project.trackingCode;

  if (org && code) return `(${org}: ${code})`;
  if (org) return `(${org})`;
  if (code) return `(${code})`;
  return null;
}

/**
 * The project line above the heading.
 *
 * Shipped and the prototype both rendered a bare project name carrying a
 * hover-only underline (the prototype's own `Header`, in
 * `components/EngagementHub/`), so nothing
 * said the line was the project and nothing said it was navigable until you
 * were already on it. ENG-5737 is a deliberate departure from the prototype,
 * asked for by product, not a drift correction.
 *
 * The arrow is the affordance, not a text decoration. App-wide, an internal
 * link carries no underline — `componentsv2/Link` sets `textDecoration: none`
 * and `components/Link` underlines external hrefs only — so a persistent
 * `arrow_back` is the only rest-state link signal available. Same recipe as
 * the consultation page's `ProjectBar`, which the report named as the pattern
 * to match. The Tailwind classes below are what implements it here.
 */
function ProjectBreadcrumb({ view, viewerRole }: HubHeaderProps) {
  // `primaryProject` is `project:view`-filtered and resolves null for the
  // expert on every engagement (§5 null-as-denial), so they get the
  // display-only `projectName` as plain text: labelled, but not a dead link,
  // and with no tracking code, which rides on the field they cannot read.
  if (!view.project) {
    if (!view.projectName) return null;
    return (
      <span className="text-on-surface-muted">
        Project: <span className="text-on-surface">{view.projectName}</span>
      </span>
    );
  }

  // `Project.name` is nullable. The label alone is still true and the link
  // still works, so an unnamed project gets a bare `Project` crumb rather than
  // the `Project: Project` the view model's old string fallback produced.
  const name = view.project.name ?? view.projectName;
  const suffix = projectSuffix(view.project, viewerRole);

  return (
    <Link
      to={`/project/${view.project.id}`}
      className="flex items-center gap-4 text-left text-on-surface-muted"
    >
      <ArrowBack className="text-brand-primary" sx={{ fontSize: 16 }} />
      <span>
        {name ? (
          <span>
            Project: <span className="text-on-surface">{name}</span>
          </span>
        ) : (
          <span>Project</span>
        )}
        {suffix ? <span>{` ${suffix}`}</span> : null}
      </span>
    </Link>
  );
}

/**
 * Which copy branch the status chip reads.
 *
 * At `pending_confirmation` the chip's copy answers one question: is the viewer
 * the one being waited on? When the customer side owes the answer, that is the
 * creator alone (`canAcceptOfferLive`). Everyone else on that side used to read
 * "Awaiting your accept" too, including the governance approver straight after
 * sending the offer back from this page (ENG-5752), who was told to accept
 * their own adjustment. They read the copy table's outside branch, "Awaiting
 * customer accept", which is the reuse `AccountRollupRow` makes for the same
 * reason.
 */
function badgeRoleFor(view: EngagementView, viewerRole: ViewerRole, viewerUserId: string) {
  if (viewerRole === 'expert' || view.createdByUserId === viewerUserId) return viewerRole;
  return 'of_fulfillment';
}

/** `kind` is an open string on the wire; only the two the copy table names count. */
function feedbackKindOf(view: EngagementView): 'adjustment' | 'rejection' | null {
  const kind = view.governanceFeedback?.kind;
  return kind === 'adjustment' || kind === 'rejection' ? kind : null;
}

export default function HubHeader({
  view,
  viewerRole,
  viewerUserId,
}: HubHeaderProps & { viewerUserId: string }): JSX.Element {
  const { nteHoursTotal, nteHoursConsumed, hoursPending } = view;
  const consumedPct = nteHoursTotal ? Math.round((nteHoursConsumed / nteHoursTotal) * 100) : 0;

  // §6.3: each side gets only its own headline figure. Both are null for a
  // viewer entitled to neither, and a null renders nothing — never a zero.
  const expertMoney = viewerSeesExpertRate(viewerRole) ? view.expertEarningsCents : null;
  const customerMoney = viewerSeesExpertRate(viewerRole) ? null : view.nteTotalCents;

  return (
    <div className="flex flex-col gap-24 border-b border-surface-border pb-24">
      <div className="flex flex-col gap-16 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-16">
          <IdentityTile view={view} viewerRole={viewerRole} />
          <div className="flex min-w-0 flex-col gap-4">
            <ProjectBreadcrumb view={view} viewerRole={viewerRole} />
            {/*
             * The heading links to the counterparty's profile only on the
             * customer side, where the counterparty is a person. For the expert
             * it is the customer organization (`customerOrganizationName`),
             * which has no profile route, so it stays plain text.
             *
             * `/profile/:id` takes a user id: api's `user(id:)` resolves
             * `FindProfileByUserID` first and only falls back to the username
             * (`user_gql.go`), so `expertIdentity.userId` is the right id space.
             */}
            <h1 className="text-on-surface hd-3">
              {viewerRole !== 'expert' && view.expertIdentity ? (
                // No hover decoration (ENG-5737). `componentsv2/Link` sets
                // `textDecoration: none` and the app underlines external links
                // only, so the underline this used to carry was a prototype
                // idiom. The same sweep removed eleven more `hover:underline`
                // sites in this tree, `OverviewPanel`'s link to *this same
                // profile* included, so the convention now holds throughout
                // `live/` rather than just here. Two deliberate exemptions:
                // the external invoice anchor in `CustomerBillingArm`, since
                // the app does underline external links, and
                // `focus-visible:underline`, since focus is a different state.
                //
                // It deliberately does not take the breadcrumb's arrow either.
                // The breadcrumb has to announce that a different page exists;
                // this heading names the page you are already on, and the
                // Participants row reaches the same profile, so leaving it
                // unmarked costs a shortcut rather than a destination.
                <Link to={`/profile/${view.expertIdentity.userId}`} className="text-on-surface">
                  {counterpartyHeading(view, viewerRole)}
                </Link>
              ) : (
                counterpartyHeading(view, viewerRole)
              )}
            </h1>
          </div>
        </div>
        {/* Live: after the governance approver's Approve on a USD-invoiced
            engagement the Overview banner unmounts outright, taking its
            `role="status"` with it, so this chip is the only confirmation. */}
        <StatusBadge
          status={view.status}
          awaiting={view.awaiting}
          viewerRole={badgeRoleFor(view, viewerRole, viewerUserId)}
          governanceFeedbackKind={feedbackKindOf(view)}
          live
        />
      </div>

      {nteHoursTotal !== null ? (
        <div className="grid grid-cols-1 gap-24 md:grid-cols-3">
          <div>
            <div className="text-on-surface-muted subtitle-2">NTE consumed</div>
            <div className="mt-4 flex items-baseline gap-8">
              <span className="text-on-surface hd-5">{formatHours(nteHoursConsumed)}</span>
              <span className="text-on-surface opacity-70 body-2">
                of {formatHours(nteHoursTotal)} ({consumedPct}%)
              </span>
            </div>
            <Meter value={consumedPct} className="mt-8 w-full" />
          </div>

          <Metric label="Pending approval">{formatHours(hoursPending)}</Metric>

          {expertMoney !== null ? (
            <Metric label="Max earnings">
              {formatEngagementMoney(expertMoney, view.currency)}
            </Metric>
          ) : customerMoney !== null ? (
            <Metric label="Not-to-exceed total">
              {formatEngagementMoney(customerMoney, view.currency)}
            </Metric>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
